import { useBreakpointAtLeast } from "@core-ui/hooks/useBreakpoint";
import { useAgentChatAutoOpen } from "@ext/agent/components/hooks/useAgentChatAutoOpen";
import { useAgentChatHealthcheckRunner } from "@ext/agent/components/hooks/useAgentChatHealthcheckRunner";
import { useAgentChatVisibility } from "@ext/agent/components/hooks/useAgentChatVisibility";
import { useAgentChatHealthcheck } from "@ext/agent/components/store/AgentChatHealthcheckStore";
import { AGENT_CHAT_PANEL_ID } from "@ext/agent/components/store/AgentChatIsOpenStore";
import t from "@ext/localization/locale/translate";
import { usePanelToggle } from "@ui-kit/FloatingPanel";
import { GlassToolbar, GlassToolbarIcon, GlassToolbarText, GlassToolbarToggleButton } from "@ui-kit/GlassToolbar";
import { useCallback, useRef } from "react";

export const AgentChatButton = () => {
	const { showToggle } = useAgentChatVisibility();
	const isLabelVisible = useBreakpointAtLeast("md");
	const { status: healthcheckStatus } = useAgentChatHealthcheck();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const { isOpen, open, close } = usePanelToggle(AGENT_CHAT_PANEL_ID, triggerRef);
	const { resolveTarget, runHealthcheck } = useAgentChatHealthcheckRunner(isOpen);
	useAgentChatAutoOpen(showToggle, isOpen, open);

	const toggleAgentChat = useCallback(() => {
		if (isOpen) {
			close();
			return;
		}

		if (healthcheckStatus === "checking") {
			open();
			return;
		}

		const target = resolveTarget();
		if (!target) return;

		open();
		void runHealthcheck(target);
	}, [isOpen, healthcheckStatus, open, close, resolveTarget, runHealthcheck]);

	if (!showToggle) return null;

	return (
		<GlassToolbar className="rounded-full" variant="single">
			<GlassToolbarToggleButton
				active={isOpen}
				aria-label={t("agent.tooltips.toggle-chat")}
				disabled={healthcheckStatus === "checking"}
				onClick={toggleAgentChat}
				ref={triggerRef}
				tooltipText={isLabelVisible ? undefined : t("agent.tooltips.toggle-chat")}
			>
				<GlassToolbarIcon icon="sparkles" />
				<GlassToolbarText className="hidden md:inline text-xs font-medium">
					{t("agent.panel-name")}
				</GlassToolbarText>
			</GlassToolbarToggleButton>
		</GlassToolbar>
	);
};
